// Chemin: C:\gestion-copro\src\types\extraction.types.ts

import { Timestamp } from 'firebase/firestore';

// ============================================
// TYPES D'EXTRACTION - DONNÉES BRUTES
// ============================================

/**
 * Lot extrait d'un règlement de copropriété
 * Certains champs peuvent être vides si non détectés
 */
export interface ExtractedLot {
  // Identifiant temporaire pour le tableau (pas encore en base)
  tempId: string;
  
  // Données extraites
  numero: string;                    // "1", "A01", etc.
  numeroCommercial?: string;         // "01-T4", "11-T3", etc.
  type: string;                      // "T2", "T3", "T4", "studio", etc.
  batiment?: string;                 // "A", "unique", etc.
  etage: string;                     // "RDC", "1er", "2ème", etc.
  description?: string;              // "avec jardin/terrasse", "avec balcon"
  tantiemesGeneraux: number;         // 619, 354, etc.
  
  // État de validation
  selected: boolean;                 // Coché pour import
  hasError: boolean;                 // Erreur détectée
  errorMessage?: string;             // Message d'erreur
}

/**
 * Membre du Conseil Syndical extrait d'un PV d'AG
 */
export interface ExtractedMembreCS {
  // Identifiant temporaire
  tempId: string;
  
  // Données extraites
  nom: string;                       // "FOUQUET", "GOMBART"
  prenom?: string;                   // Souvent absent des PV
  role: 'president' | 'vice_president' | 'secretaire' | 'tresorier' | 'membre';
  dateElection?: string;             // "18/07/2025"
  dateFinMandat?: string;            // "18/07/2027"
  
  // À compléter manuellement
  lotId?: string;                    // Lien vers lot (dropdown)
  lotNumero?: string;                // Numéro du lot
  email?: string;
  telephone?: string;
  
  // État de validation
  selected: boolean;
  hasError: boolean;
  errorMessage?: string;
}

// ============================================
// RÉSULTAT D'EXTRACTION
// ============================================

export type ExtractionType = 'lots' | 'conseil_syndical' | 'interlocuteurs';

export interface ExtractionResult<T> {
  success: boolean;
  type: ExtractionType;
  documentName: string;
  extractedAt: Date;
  
  // Données extraites
  items: T[];
  totalFound: number;
  
  // Métadonnées
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
  errors: string[];
  
  // Source
  sourceDocumentId?: string;         // Si document déjà archivé
}

export type LotsExtractionResult = ExtractionResult<ExtractedLot>;
export type CSExtractionResult = ExtractionResult<ExtractedMembreCS>;

// ============================================
// ÉTAT DU WORKFLOW D'EXTRACTION
// ============================================

export type ExtractionStep = 
  | 'idle'           // Pas d'extraction en cours
  | 'uploading'      // Document en cours d'upload
  | 'analyzing'      // Claude analyse le document
  | 'reviewing'      // Utilisateur vérifie les données
  | 'importing'      // Import en cours
  | 'completed'      // Terminé
  | 'error';         // Erreur

export interface ExtractionState {
  step: ExtractionStep;
  type?: ExtractionType;
  result?: LotsExtractionResult | CSExtractionResult;
  error?: string;
  progress?: number;                 // 0-100 pour la barre de progression
}

// ============================================
// MAPPING VERS TYPES FIRESTORE
// ============================================

/**
 * Convertit un ExtractedLot validé en données pour Firestore
 */
export interface LotCreateData {
  coproprieteId: string;
  numero: string;
  type: 'appartement' | 'studio' | 'parking' | 'cave' | 'local_commercial' | 'bureau' | 'autre';
  batiment?: string;
  etage?: string;
  description?: string;
  tantiemesGeneraux: number;
  tantiemesSpeciaux?: Record<string, number>;
  proprietaire?: {
    nom?: string;
    prenom?: string;
    email?: string;
    telephone?: string;
    userId?: string;
  };
}

/**
 * Convertit un ExtractedMembreCS validé en données pour Firestore
 */
export interface MembreCSCreateData {
  coproprieteId: string;
  nom: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  lotId?: string;
  lotNumero?: string;
  userId?: string;
  role: 'president' | 'vice_president' | 'secretaire' | 'tresorier' | 'membre';
  dateElection?: Timestamp;
  agElectionRef?: string;
  dateFinMandat?: Timestamp;
  actif: boolean;
  notes?: string;
}

// ============================================
// HELPERS
// ============================================

/**
 * Mapping type logement extrait → type Firestore
 */
export const mapExtractedTypeToLotType = (
  extractedType: string
): 'appartement' | 'studio' | 'parking' | 'cave' | 'local_commercial' | 'bureau' | 'autre' => {
  const normalized = extractedType.toLowerCase().trim();
  
  if (normalized.includes('t1') || normalized.includes('studio')) return 'studio';
  if (normalized.includes('t2') || normalized.includes('t3') || 
      normalized.includes('t4') || normalized.includes('t5') ||
      normalized.includes('logement') || normalized.includes('appartement')) return 'appartement';
  if (normalized.includes('parking') || normalized.includes('stationnement')) return 'parking';
  if (normalized.includes('cave')) return 'cave';
  if (normalized.includes('local') && normalized.includes('vélo')) return 'cave'; // Local vélo → cave
  if (normalized.includes('commercial') || normalized.includes('boutique')) return 'local_commercial';
  if (normalized.includes('bureau')) return 'bureau';
  
  return 'autre';
};

/**
 * Génère un ID temporaire unique
 */
export const generateTempId = (): string => {
  return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};